import PropTypes from "prop-types";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import ArrowRightRounded from "@mui/icons-material/ArrowRightRounded";
import Autocomplete from "@mui/material/Autocomplete";
import { useState, useEffect, useRef } from "react";
import { useDynamicTranslation } from "@/hooks/useDynamicTranslation";

export function KeyValue({
  label,
  value,
  type = "standart",
  options,
  loadData,
  onChange,
  enableSearchIcon,
  onSearchClick,
}: any) {
  const { t } = useDynamicTranslation("shared");

  return (
    <Box>
      <Grid container spacing={1} alignItems="center">
        <Grid item>
          <TextField value={label} />
        </Grid>
        <Grid item>
          <ArrowRightRounded />
        </Grid>
        <Grid item>
          {type === "autocomplete" ? (
            <AsyncAutoComplete
              label={label}
              value={value}
              options={options}
              loadData={loadData}
              onChange={onChange}
            />
          ) : (
            <TextField
              value={value}
              label={t("value")}
              onChange={(e: any) => onChange(e.target.value)}
              InputProps={{
                endAdornment: enableSearchIcon ? (
                  <IconButton onClick={() => onSearchClick()}>
                    <SearchIcon />
                  </IconButton>
                ) : null,
              }}
            />
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

function AsyncAutoComplete({ label, value, options, loadData, onChange }: any) {
  const isInitialLoad = useRef(true);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && loadData && isInitialLoad.current) {
      setLoading(true);
      loadData();
      isInitialLoad.current = false;
    }
  }, [open]);

  useEffect(() => {
    setLoading(false);
  }, [options]);

  return (
    <Autocomplete
      open={open}
      value={value}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onChange={(_e: any, option: any) => onChange(option?.value)}
      loading={loading}
      options={options}
      isOptionEqualToValue={(option: any, val: any) => option.value === val}
      getOptionLabel={(option: any) =>
        typeof option === "object"
          ? option.label
          : (options.find((o: any) => o.value === option)?.label ?? "")
      }
      renderInput={(params: any) => (
        <TextField
          {...params}
          label={label}
          sx={{ minWidth: 200 }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

KeyValue.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.any,
  type: PropTypes.oneOf(["standart", "autocomplete"]),
  options: PropTypes.array,
  loadData: PropTypes.func,
  onChange: PropTypes.func,
  enableSearchIcon: PropTypes.bool,
  onSearchClick: PropTypes.func,
};

AsyncAutoComplete.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.any,
  options: PropTypes.array,
  loadData: PropTypes.func,
  onChange: PropTypes.func,
};
